import type { ApiEnvelope, ProblemDetails } from '@orion/contracts';

/**
 * A small hand-written client for now — `packages/api-client`
 * (docs/architecture/04-repository-structure.md) generates a typed one from
 * the OpenAPI spec once that's published (M6). Every call goes through the
 * Vite dev proxy / same-site production path, so the session cookie is
 * always first-party (docs/architecture/02 § "one site").
 */
export class ApiError extends Error {
  constructor(
    public readonly problem: ProblemDetails,
    public readonly status: number,
  ) {
    super(problem.title);
  }
}

let csrfToken: string | null = null;

/** Called once a session (login or GET /auth/session) hands back a fresh
 * token (docs/architecture/05-authentication.md § 5 — synchronizer CSRF). */
export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

/** For call sites that can't go through `request()` above (e.g. a
 * multipart file upload, which needs FormData rather than a JSON body) but
 * still need to attach the same CSRF header by hand. */
export function getCsrfToken(): string | null {
  return csrfToken;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'include',
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(csrfToken && method !== 'GET' ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  const payload = (await res.json().catch(() => null)) as ApiEnvelope<T> | ProblemDetails | null;

  if (!res.ok) {
    const problem = (payload as ProblemDetails) ?? {
      type: 'about:blank',
      title: 'Something went wrong',
      status: res.status,
      code: 'unknown_error',
      errorId: 'n/a',
    };
    throw new ApiError(problem, res.status);
  }

  return (payload as ApiEnvelope<T>).data;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/** List endpoints return `{ data, meta }` — this keeps both instead of
 * discarding `meta` the way the plain `api.get` helper does. */
export async function getPage<T>(path: string): Promise<{ data: T; meta?: ApiEnvelope<T>['meta'] }> {
  const res = await fetch(`/api/v1${path}`, { credentials: 'include' });
  const payload = (await res.json()) as ApiEnvelope<T>;
  if (!res.ok) throw new ApiError(payload as unknown as ProblemDetails, res.status);
  return { data: payload.data, meta: payload.meta };
}
